import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  getSegmentColorPalette,
  getSelectedSegment,
  getWheelColorPalette,
} from "../redux/selectors/segmentBuilderSelectors";
import { useWheelSegments } from "../contexts/wheelSegments";
import { getSelectedSegmentColor, getSelectedSegmentPalette, getSelectedWheelPalette } from "../redux/selectors/colorPalette";
import { isUsableColor, deriveChartColor } from "../utils/chartColor";

export const usePaletteColor = () => {
  const url = window.location.href;
  const isViewWheelPage = url.includes("wheel");
  const isMobileView = url.includes("wheel/segment")

  const segmentBuilderSegment = useSelector(getSelectedSegment) || {};
  const { segments,wheelData } = useWheelSegments();

  const wheelPalette = useSelector(getSelectedWheelPalette) //for web view in mobile
  const segmentPalette = useSelector(getSelectedSegmentPalette) //for web view in mobile
  const segmentColor = useSelector(getSelectedSegmentColor)

  const selectedArc =
    (isViewWheelPage
      ? segments.find((seg) => seg.selected)
      : segmentBuilderSegment) || {};

  const { settings: segmentSettings = {} } = selectedArc;
  const { color: selectedSegmentColor } = segmentSettings;
  const segmentColorPalette = useSelector(getSegmentColorPalette);
  const selectedPalette = isMobileView ? segmentPalette :
    (isViewWheelPage ? segmentSettings.paletteData : segmentColorPalette) || {};

  const wheelColorPalette = useSelector(getWheelColorPalette) || {};
  const selectedWheelPalette = isMobileView ? wheelPalette : (isViewWheelPage ? wheelData.palette_data : wheelColorPalette) || {};

  const isValidSuggestedColor = (suggestedColorString = "") => {
    return (
      String(suggestedColorString).startsWith("suggested_color") ||
      String(suggestedColorString).startsWith("wheel_suggested_color")
    );
  };
  const getSuggestedColor = (colorString = "") => {
    const isWheelSuggested = colorString.startsWith("wheel_suggested_color_");
    const palette = isWheelSuggested ? selectedWheelPalette : selectedPalette;
    const segmentFallback = segmentColor || selectedSegmentColor;

    // Ensure palette colors are available
    if (!Array.isArray(palette?.colors) || palette?.colors.length === 0) {
      return segmentFallback;
    }

    // Extract index from color string
    const match = colorString.match(/(?:wheel_)?suggested_color_(\d+)/);
    const index = match ? parseInt(match[1], 10) : -1;

    // Return the color hex if valid index AND that specific hex wasn't wiped
    // (a single palette entry cleared to "" or "#fff"), else fall back to the
    // segment color same as the out-of-range/empty-palette cases above —
    // never leak a blank/white hex through.
    const paletteHex = index >= 0 && index < palette.colors.length ? palette.colors[index]?.hex : undefined;
    return isUsableColor(paletteHex) ? paletteHex : segmentFallback;
  };

  const getPaletteColor = (color) => {
    return isValidSuggestedColor(color) ? getSuggestedColor(color) : color;
  };

  // The Graph block's per-datapoint chart fallback: when a datapoint carries
  // no color reference at all, derive one from the segment's own color, else
  // the segment palette's primary, else the WHEEL palette's primary, shaded
  // by index — so a missing/wiped color never defaults to white/transparent
  // or a generic constant while a real wheel palette color is available
  // (gordon-0915: "...or the wheel palette", never white-on-light).
  const getFallbackColor = (index = 0) => {
    const base = [
      segmentColor,
      selectedSegmentColor,
      selectedPalette?.colors?.[0]?.hex,
      selectedWheelPalette?.colors?.[0]?.hex,
    ].find(isUsableColor);
    return deriveChartColor(undefined, base, index);
  };

  // T22.138: the segment's own colour (the same fallback getSuggestedColor uses),
  // for surfaces that follow the segment when the block sets no colour.
  const getSegmentColor = () =>
    [segmentColor, selectedSegmentColor].find(isUsableColor);

  return useMemo(
    () => ({
      getPaletteColor,
      getFallbackColor,
      getSegmentColor,
    }),
    [selectedPalette, selectedWheelPalette, segmentColor, selectedSegmentColor]
  );
};
